/**
 * Sentinel for a `/sector/$sectorId` path param that fails `SectorIdSchema`.
 *
 * The sibling of `stock/invalid-ticker-param-error.ts`, and it exists for the
 * same reason: TanStack Router's CatchBoundary routes BOTH `params.parse`
 * failures and render-time exceptions from the route subtree to one
 * `errorComponent`, so the boundary needs a discriminant. Without one, a crash
 * inside the correlation matrix would be reported as "unknown sector" — a wrong
 * answer that reads as a correct one (plan §1.4 · error §6).
 *
 * Until Phase 8 the sector route had NO error component at all, so a bad id
 * escaped to the router's default fallback and rendered the raw Zod issue —
 * including the entire internal sector-id enum — as page content.
 */

import { errorHasKind } from '@/lib/errors/error-has-kind';

/** Discriminant, so a catch site does not depend on `instanceof` surviving a bundler. */
const INVALID_SECTOR_PARAM = 'invalid-sector-param';

export class InvalidSectorParamError extends Error {
  public readonly kind = INVALID_SECTOR_PARAM;

  public constructor() {
    super('INVALID_SECTOR_ID');
    this.name = 'InvalidSectorParamError';
  }
}

/**
 * Not a type predicate: a true result may describe the CAUSE of the wrapper
 * that was passed in, not the wrapper itself.
 */
export function isInvalidSectorParamError(error: unknown): boolean {
  return errorHasKind(error, INVALID_SECTOR_PARAM);
}
